import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import CommunityPostCard from "./CommunityPostCard";
import GiftIcon from "./GiftIcon";
import { Gift, Trophy, Bookmark, Sparkles, Plus, Lock } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";

const TABS = [
  { key: "posts",        label: "Posts",        Icon: Sparkles, ownerOnly: false },
  { key: "gifts",        label: "Gifts",        Icon: Gift,      ownerOnly: false },
  { key: "achievements", label: "Achievements", Icon: Trophy,    ownerOnly: false },
  { key: "saved",        label: "Saved",        Icon: Bookmark,  ownerOnly: true },
];

/**
 * ProfileTabs — Posts | Gifts | Achievements | Saved.
 *
 * Saved is owner-only and locked to visitors. Backend also protects it.
 *
 * Props:
 *   userId, isOwner
 */
export default function ProfileTabs({ userId, isOwner }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activePost, setActivePost] = useState(null);

  const visibleTabs = TABS;  // Saved tab stays visible for visitors but is locked

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/community/posts", { params: { author_id: userId, limit: 60 } });
      setPosts(data);
    } finally { setLoading(false); }
  }, [userId]);

  const loadGifts = useCallback(async () => {
    setLoading(true);
    try {
      // Public projection of a user's gift activity — endpoint reuses the
      // existing gifts feed with a user filter. If not available, this
      // gracefully falls back to an empty state.
      const { data } = await api.get(`/gifts/on/user/${userId}`).catch(() => ({ data: { items: [] } }));
      setGifts(data.items || data || []);
    } finally { setLoading(false); }
  }, [userId]);

  const loadAchievements = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${userId}/achievements`);
      setAchievements(data.items || []);
    } finally { setLoading(false); }
  }, [userId]);

  const loadSaved = useCallback(async () => {
    if (!isOwner) return;
    setLoading(true);
    try {
      const { data } = await api.get("/users/me/saved-posts");
      setSaved(data.items || []);
    } finally { setLoading(false); }
  }, [isOwner]);

  useEffect(() => {
    if (tab === "posts") loadPosts();
    else if (tab === "gifts") loadGifts();
    else if (tab === "achievements") loadAchievements();
    else if (tab === "saved") loadSaved();
  }, [tab, loadPosts, loadGifts, loadAchievements, loadSaved]);

  return (
    <div className="space-y-3" data-testid="profile-tabs">
      <nav className="flex gap-1 overflow-x-auto no-scrollbar sticky top-0 bg-zinc-950/80 backdrop-blur -mx-1 px-1 py-2 z-10">
        {visibleTabs.map(({ key, label, Icon, ownerOnly }) => {
          const locked = ownerOnly && !isOwner;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-[11px] uppercase tracking-widest font-black transition-colors ${
                tab === key
                  ? "bg-pink-500/20 text-pink-200 border-pink-400/50"
                  : locked
                  ? "bg-white/[0.02] text-zinc-500 border-white/5 hover:bg-white/[0.04]"
                  : "bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10"
              }`}
              data-testid={`profile-tab-${key}`}
              aria-label={locked ? `${label} (private)` : label}
            >
              {locked ? <Lock className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />} {label}
            </button>
          );
        })}
      </nav>

      {loading && (
        <div className="text-center py-10 text-zinc-500 text-sm">Loading…</div>
      )}

      {!loading && tab === "posts" && (
        posts.length === 0 ? (
          <div className="text-center py-10 text-zinc-500 border border-dashed border-white/10 rounded-3xl" data-testid="profile-tab-posts-empty">
            <Plus className="w-6 h-6 mx-auto mb-2" />
            <p className="text-sm">{isOwner ? "Share a photo or video." : "No posts yet."}</p>
            {isOwner && (
              <Button
                onClick={() => navigate("/create-post")}
                className="mt-4 h-10 px-5 rounded-full bg-pink-500 hover:bg-pink-400 text-white text-xs font-bold"
                data-testid="profile-tab-posts-create"
              >
                Create your first post
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((p) => (
              <CommunityPostCard
                key={p.post_id}
                post={p}
                onOpen={(post) => setActivePost(post)}
                onChange={(np) => setPosts((arr) => arr.map((x) => (x.post_id === np.post_id ? { ...x, ...np } : x)))}
                onDelete={(id) => setPosts((arr) => arr.filter((x) => x.post_id !== id))}
              />
            ))}
          </div>
        )
      )}

      {!loading && tab === "gifts" && (
        gifts.length === 0 ? (
          <div className="text-center py-10 text-zinc-500 border border-dashed border-white/10 rounded-3xl" data-testid="profile-tab-gifts-empty">
            <Gift className="w-6 h-6 mx-auto mb-2" />
            <p className="text-sm">No gift activity yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="profile-tab-gifts-list">
            {gifts.map((g, i) => (
              <div key={g.tx_id || g.gift_id || i} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 flex items-center gap-2">
                <GiftIcon gift={g.gift_snapshot || g} size={28} />
                <div className="min-w-0">
                  <p className="text-xs font-bold truncate">{g.gift_snapshot?.name || g.name}</p>
                  {g.sender_name && (
                    <p className="text-[10px] text-zinc-500 truncate">from {g.sender_name}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {!loading && tab === "achievements" && (
        achievements.length === 0 ? (
          <div className="text-center py-10 text-zinc-500 border border-dashed border-white/10 rounded-3xl" data-testid="profile-tab-achievements-empty">
            <Trophy className="w-6 h-6 mx-auto mb-2" />
            <p className="text-sm">No achievements unlocked yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="profile-tab-achievements-list">
            {achievements.map((a) => (
              <div
                key={a.id}
                className="rounded-2xl border p-3"
                style={{ borderColor: `${a.color || "#39FF14"}55`, background: `${a.color || "#39FF14"}12` }}
              >
                <p className="text-[10px] uppercase tracking-widest font-black" style={{ color: a.color || "#39FF14" }}>
                  {a.slot?.replace("_", " ")}
                </p>
                <p className="text-sm font-black mt-0.5 truncate">{a.label}</p>
                {a.description && (
                  <p className="text-[10px] text-zinc-400 mt-1 line-clamp-2">{a.description}</p>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {!loading && tab === "saved" && isOwner && (
        saved.length === 0 ? (
          <div className="text-center py-10 text-zinc-500 border border-dashed border-white/10 rounded-3xl" data-testid="profile-tab-saved-empty">
            <Bookmark className="w-6 h-6 mx-auto mb-2" />
            <p className="text-sm">Nothing saved yet.</p>
            <p className="text-[10px] mt-1">Tap the bookmark on any post to keep it here.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="profile-tab-saved-list">
            {saved.map((p) => (
              <CommunityPostCard
                key={p.post_id}
                post={p}
                onOpen={(post) => setActivePost(post)}
                onChange={() => {}}
                onDelete={() => {}}
              />
            ))}
          </div>
        )
      )}

      {!loading && tab === "saved" && !isOwner && (
        <div className="text-center py-10 text-zinc-500 border border-dashed border-white/10 rounded-3xl" data-testid="profile-tab-saved-locked">
          <Lock className="w-6 h-6 mx-auto mb-2" />
          <p className="text-sm">Saved items are private.</p>
        </div>
      )}
    </div>
  );
}
